/*
 * Zentraler API-Client mit Timeout, Fehlernormalisierung und Basis-Methoden.
 */

import { getApiBaseUrl } from '../config/apiBaseUrl';

const DEFAULT_TIMEOUT_MS = 15000;

class ApiError extends Error {
  constructor(message, options = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = options.status ?? null;
    this.code = options.code ?? null;
    this.details = options.details ?? null;
  }
}

class TimeoutError extends Error {
  constructor(message = 'Request timed out') {
    super(message);
    this.name = 'TimeoutError';
  }
}

function withTimeout(promise, timeoutMs, controller) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      controller.abort();
      reject(new TimeoutError());
    }, timeoutMs > 0 ? timeoutMs : DEFAULT_TIMEOUT_MS);

    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
}

async function parseJsonSafe(response) {
  try {
    return await response.json();
  } catch (e) {
    return null;
  }
}

async function request(path, options = {}) {
  const controller = new AbortController();
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const url = `${getApiBaseUrl()}${path}`;

  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {})
  };

  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('authToken');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  const fetchOptions = {
    method: options.method || 'GET',
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
    signal: controller.signal,
    credentials: options.credentials || 'include'
  };

  try {
    const response = await withTimeout(fetch(url, fetchOptions), timeoutMs, controller);
    const data = await parseJsonSafe(response);

    if (!response.ok) {
      // Extrahiere Fehlermeldung aus verschiedenen möglichen Formaten
      let message = `HTTP ${response.status}`;
      if (data) {
        if (data.message) {
          message = data.message;
        } else if (data.error) {
          if (typeof data.error === 'string') {
            message = data.error;
          } else if (data.error.message) {
            message = data.error.message;
          } else if (Array.isArray(data.error.validationErrors)) {
            message = data.error.validationErrors.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
          }
        } else if (data.errors) {
          if (Array.isArray(data.errors)) {
            message = data.errors.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
          } else if (typeof data.errors === 'string') {
            message = data.errors;
          }
        }
      }
      throw new ApiError(message, {
        status: response.status,
        details: data || null
      });
    }

    // Falls Backend ein Standardformat { success, data } nutzt (optional vollständige Antwort, z. B. für view-Meta)
    const unwrap = options.unwrapResponse !== false;
    if (
      unwrap &&
      data &&
      typeof data === 'object' &&
      'success' in data &&
      'data' in data
    ) {
      return data.data;
    }

    return data;
  } catch (err) {
    if (err.name === 'AbortError') {
      throw new TimeoutError();
    }
    throw err;
  }
}

export const apiClient = {
  get: (path, options = {}) => request(path, { ...options, method: 'GET' }),
  post: (path, body, options = {}) => request(path, { ...options, method: 'POST', body }),
  put: (path, body, options = {}) => request(path, { ...options, method: 'PUT', body }),
  patch: (path, body, options = {}) => request(path, { ...options, method: 'PATCH', body }),
  delete: (path, options = {}) => request(path, { ...options, method: 'DELETE' }),
  get baseUrl() {
    return getApiBaseUrl();
  }
};

export { ApiError, TimeoutError };
